import React from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, Search, X, XCircle } from 'lucide-react';
import { C } from '../../theme.js';
import * as api from '../../api.js';
import { Button, Eyebrow, IconButton } from '../../components/Primitives.jsx';
import { Modal } from '../../components/Modal.jsx';
import { SNIPPET_CATEGORIES, applySnippet, applySnippetIndented, expandSnippet, snippetsFor, uniqueName } from './codeMode/snippets.js';
import { columnValues, describeResult, detectDateFormat, insertOptions, looksLikeDates } from './codeMode/dateFormatDetector.js';
import { HELPERS } from './codeMode/helpersReference.js';
import { columnRef, insertAtCursor, storedAsText } from './codeMode/columnRef.js';
import { analyzeBodyClient, analyzeClient, loadAcorn } from './codeMode/clientAnalysis.js';
import { firstColumnName, insertTab, objectKey, starterTemplate } from './codeMode/valueBodyTemplates.js';
import { buildValueBodyRequest, mapValueBodyResponse } from './codeMode/valueBodyPreview.js';
import { ValueBodyPreview } from './ValueBodyPreview.jsx';
import { MAX_EXPRESSION_CHARS, lengthStatus, previewSummary, validationState } from './codeMode/validationMessages.js';
import { FIRST_OPEN_HINT, mapConvertBlockResponse, needsOverwriteConfirm, noticesBox } from './codeMode/convertFromBuilder.js';

// ── Expression editor window ─────────────────────────────────────────────
// Built around a slot `kind`: "condition" (one true/false expression, kept for
// the legacy per-case view) or "value_body" (a function body returning the
// block's output value(s)). The snippet palette and the preview endpoint are
// both keyed by it. The server's /expr-preview
// answer is authoritative; the client-side acorn pass only gives instant
// syntax feedback and the column chips between responses.

const MONO = "'JetBrains Mono', ui-monospace, monospace";
const DEBOUNCE_MS = 300;
const ROW_LIST = 20;
const ROW_CAP = 200;

const TYPE_BADGE = { number: '#', date: '◷', string: 'Aa' };

function Panel({ title, children, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, ...style }}>
      <div style={{ padding: '10px 12px 6px' }}><Eyebrow>{title}</Eyebrow></div>
      {children}
    </div>
  );
}

function SmallInput({ value, onChange, placeholder }) {
  return (
    <div style={{ position: 'relative', margin: '0 12px 8px' }}>
      <Search size={13} style={{ position: 'absolute', left: 8, top: 8, color: C.fg5 }} />
      <input
        value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box', background: C.input, color: C.fg2,
          border: `1px solid ${C.borderStrong}`, borderRadius: 6, padding: '6px 8px 6px 26px',
          fontSize: '.78rem', fontFamily: 'inherit', outline: 'none',
        }}
      />
    </div>
  );
}

function ExampleLines({ title, count, examples }) {
  return (
    <div style={{ background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem', marginTop: 8 }}>
      <div style={{ fontWeight: 700 }}>{title}: {count} row{count === 1 ? '' : 's'} (counted as not matching)</div>
      {examples.map((e) => (
        <div key={e.row} style={{ fontFamily: MONO, fontSize: '.74rem', marginTop: 2 }}>row {e.row}: {e.message}</div>
      ))}
    </div>
  );
}

export function ExpressionEditorModal({
  kind = 'condition', title = 'Expression editor', caseLabel,
  hint = null,                     // condition: one line saying what true/false does here
  initialSrc = '', columns = [], rows = [], rowsNote = null, rowsOrigin = 'none',
  outputs = [],                    // value_body: declared output names (primary first)
  outputError = null,              // value_body: why the output names are invalid, or null
  convertConfig = null,            // the block config when it has builder cases (value_body only)
  showConvertHint = false,         // first open of Code on a block that has builder rules
  pipelineId, onApply, onClose,
}) {
  const isBody = kind === 'value_body';
  // An empty value_body opens on a starter template; it is only a draft until
  // Apply, and it is the baseline for "unsaved changes".
  const [baseline] = React.useState(() => (isBody && initialSrc.trim() === '' ? starterTemplate(outputs, firstColumnName(columns)) : initialSrc));
  const [src, setSrc] = React.useState(baseline);
  const [acorn, setAcorn] = React.useState(null);
  const [response, setResponse] = React.useState(null);
  const [responseSrc, setResponseSrc] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [pending, setPending] = React.useState(false);
  const [colQuery, setColQuery] = React.useState('');
  const [category, setCategory] = React.useState('all');
  const [snipQuery, setSnipQuery] = React.useState('');
  const [converting, setConverting] = React.useState(false);
  const [flash, setFlash] = React.useState(null);               // {start, end} of the last insertion
  const [showHow, setShowHow] = React.useState(false);
  const [showHelpers, setShowHelpers] = React.useState(false);
  const [dateOpen, setDateOpen] = React.useState(null);        // column whose date hint panel is open
  const flashTimer = React.useRef(null);
  const backdropRef = React.useRef(null);
  const [convertInfo, setConvertInfo] = React.useState(null);   // mapConvertBlockResponse() result
  const taRef = React.useRef(null);
  const seqRef = React.useRef(0);
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  const colNames = React.useMemo(() => columns.map(c => c.name), [columns]);
  const colKey = colNames.join('\u0001');       // stable across re-renders that rebuild `columns`
  const colNamesRef = React.useRef(colNames);
  colNamesRef.current = colNames;
  const dirty = src !== baseline;
  const outputsKey = outputs.join('\u0001');
  const outputsRef = React.useRef(outputs);
  outputsRef.current = outputs;
  const len = lengthStatus(src);
  const blank = src.trim() === '';

  // acorn is only needed once the window opens.
  React.useEffect(() => {
    let live = true;
    loadAcorn().then((m) => { if (live) setAcorn(m.default?.parse ? m.default : m); }).catch(() => {});
    return () => { live = false; };
  }, []);

  // Debounced server check; a sequence number makes stale answers harmless.
  React.useEffect(() => {
    const my = ++seqRef.current;
    if (blank || len.over || !pipelineId || (isBody && outputError)) {
      setPending(false);
      if (blank) { setResponse(null); setResponseSrc(null); setError(null); }
      return undefined;
    }
    setPending(true);
    const handle = setTimeout(async () => {
      try {
        const r = await api.exprPreview(pipelineId, isBody
          ? buildValueBodyRequest({ src, rows: rowsRef.current, columns: colNamesRef.current, outputs: outputsRef.current })
          : { src, kind, rows: rowsRef.current.slice(0, ROW_CAP), availableColumns: colNamesRef.current });
        if (my !== seqRef.current) return;
        setResponse(r); setResponseSrc(src); setError(null);
      } catch (e) {
        if (my !== seqRef.current) return;
        setError(e?.message || 'Could not reach the server.'); setResponse(null); setResponseSrc(null);
      } finally {
        if (my === seqRef.current) setPending(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [src, kind, pipelineId, colKey, blank, len.over, isBody, outputError, outputsKey]);

  const current = responseSrc === src ? response : null;      // answer for exactly this text
  const shown = current || response;                          // keep the last answer, dimmed, while checking
  const client = React.useMemo(() => (acorn ? (isBody ? analyzeBodyClient(acorn, src) : analyzeClient(acorn, src)) : null), [acorn, src, isBody]);
  const vstate = validationState({ src, response: current, error, pending });
  const clientError = !current && client && client.error ? client.error : null;
  const chipCols = current ? current.columns : (client?.columns || []);
  const known = new Set(colNames);
  const unknown = current ? current.unknownColumns : chipCols.filter(c => !known.has(c));

  const requestClose = () => {
    // Esc inside the text box first leaves the box (so Tab then moves on
    // instead of inserting spaces); a second Esc closes.
    if (taRef.current && document.activeElement === taRef.current) {
      taRef.current.blur();
      return;
    }
    if (dirty && !window.confirm('Discard your changes to this expression?')) return;
    onClose();
  };

  // Convert the block's dropdown logic into code. The result is only a draft
  // in the box: it takes effect on Apply, like typed code; the dropdown cases
  // are never touched.
  const convert = async () => {
    if (!convertConfig || converting || !pipelineId) return;
    if (needsOverwriteConfirm(src, baseline) && !window.confirm('Replace the code in the editor with the converted dropdown logic?')) return;
    setConverting(true);
    setConvertInfo(null);
    try {
      const res = mapConvertBlockResponse(await api.exprConvertBlock(pipelineId, convertConfig));
      setConvertInfo(res);
      if (res.ok) {
        setSrc(res.src);
        requestAnimationFrame(() => taRef.current?.focus());
      }
    } catch (e) {
      setConvertInfo({ ok: false, problems: [], notices: [], message: e?.message || 'Could not reach the server.' });
    } finally {
      setConverting(false);
    }
  };

  const mutate = (fn) => {
    const ta = taRef.current;
    const s = ta ? ta.selectionStart : src.length;
    const e = ta ? ta.selectionEnd : src.length;
    const r = fn(src, s, e);
    setSrc(r.text);
    if (r.hlEnd > r.hlStart) {
      setFlash({ start: r.hlStart, end: r.hlEnd, text: r.text });
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 1400);
    }
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(r.selStart, r.selEnd);
    });
  };

  React.useEffect(() => () => clearTimeout(flashTimer.current), []);
  const snippets = snippetsFor(kind, category, snipQuery).map(sn => (isBody ? expandSnippet(sn, outputs) : sn));
  const onTextKey = (e) => {
    if (!isBody || e.key !== 'Tab' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    const r = insertTab(src, e.target.selectionStart, e.target.selectionEnd);
    setSrc(r.text);
    requestAnimationFrame(() => { const el = taRef.current; if (el) el.setSelectionRange(r.selStart, r.selEnd); });
  };
  const q = colQuery.trim().toLowerCase();
  const shownCols = q ? columns.filter(c => c.name.toLowerCase().includes(q)) : columns;
  const textStored = React.useMemo(() => {
    const s = new Set();
    for (const c of columns) if (c.type === 'number' && storedAsText(rows, c.name)) s.add(c.name);
    return s;
  }, [columns, rows]);

  const dateInfo = React.useMemo(() => {
    const m = new Map();
    for (const c of columns) {
      const r = detectDateFormat(columnValues(rows, c.name));
      if (looksLikeDates(r)) m.set(c.name, r);
    }
    return m;
  }, [columns, rows]);
  // An explicit click on one of the offered options is the only way a format
  // reaches the text.
  const insertDate = (col, format) => mutate((t, a, b) => {
    const text = format === null
      ? `toDate(${columnRef(col)})`
      : `const ${uniqueName(t, 'd')} = toDate(${columnRef(col)}, ${JSON.stringify(format)});`;
    return insertAtCursor(t, a, b, text);
  });

  const issueBlocksApply = current && current.issues?.length > 0;
  const failureBlocksApply = !!(current && current.failure);
  const canApply = !blank && !len.over && !issueBlocksApply && !failureBlocksApply && !(isBody && outputError);
  const applyTitle = isBody && outputError ? outputError
    : blank ? (isBody ? 'Write the code first' : 'Write an expression first')
    : len.over ? `Over the ${MAX_EXPRESSION_CHARS}-character limit`
      : issueBlocksApply || failureBlocksApply ? 'Fix the problem shown above first' : undefined;

  const listRows = !isBody && current && current.flags ? rows.slice(0, Math.min(ROW_LIST, current.flags.length)) : [];
  const flagged = new Set([...(current?.rowErrors?.examples || []), ...(current?.nonBoolean?.examples || [])].map(e => e.row));
  const refCols = (current?.columns || []).slice(0, 6);

  return (
    <Modal onClose={requestClose} dirty={dirty} label={title}>
      {/* Title bar */}
      <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, background: C.bar }}>
        <div style={{ fontWeight: 700, color: C.fg1, fontSize: '.95rem' }}>{title}</div>
        {caseLabel && <span style={{ color: C.fg4, fontSize: '.78rem' }}>{caseLabel}</span>}
        <div style={{ flex: 1 }} />
        <span style={{ color: C.fg5, fontSize: '.74rem' }}>
          {isBody ? 'Return the value for each row' : 'Returns true or false for each row'} · helpers: num() isBlank() text() strip() + dates
        </span>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowHelpers(v => !v)} aria-expanded={showHelpers} style={{
            cursor: 'pointer', fontFamily: 'inherit', fontSize: '.72rem', fontWeight: 700, padding: '2px 10px', borderRadius: 10,
            color: C.accentBright, background: showHelpers ? 'var(--info-bg)' : 'transparent', border: `1px solid ${C.accentMuted}`,
          }}>Helpers</button>
          {showHelpers && (
            <div role="dialog" aria-label="Helpers" style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 440, maxHeight: 420, overflowY: 'auto', zIndex: 5,
              background: C.app, border: `1px solid ${C.borderStrong}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', padding: 10,
            }}>
              {['Values', 'Date'].map((g) => (
                <div key={g} style={{ marginBottom: 8 }}>
                  <Eyebrow style={{ marginBottom: 4 }}>{g === 'Values' ? 'Values' : 'Dates (calendar dates, no time zone)'}</Eyebrow>
                  {HELPERS.filter(h => h.group === g).map((h) => (
                    <div key={h.name} style={{ padding: '3px 0', borderTop: `1px solid ${C.border}` }}>
                      <div style={{ fontFamily: MONO, fontSize: '.76rem', color: C.accentBright }}>{h.signature}</div>
                      <div style={{ color: C.fg4, fontSize: '.72rem' }}>{h.description}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <IconButton title="Close" onClick={requestClose}><X size={16} /></IconButton>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr) 290px' }}>
        {/* Columns */}
        <Panel title="Columns" style={{ borderRight: `1px solid ${C.border}` }}>
          {isBody && (
            <div style={{ padding: '0 12px 8px' }}>
              <div style={{ color: C.fg5, fontSize: '.7rem', marginBottom: 4 }}>Outputs (click to insert the key)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {outputs.filter(o => o.trim() !== '').map((o) => (
                  <button key={o} onClick={() => mutate((t, a, b) => insertAtCursor(t, a, b, objectKey(o)))}
                    title={`Insert ${objectKey(o)}`}
                    style={{ fontFamily: MONO, fontSize: '.72rem', cursor: 'pointer', padding: '1px 8px', borderRadius: 10, color: C.accentBright, background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}` }}>{o}</button>
                ))}
                {outputs.every(o => o.trim() === '') && <span style={{ color: C.fg5, fontSize: '.72rem' }}>No output names yet</span>}
              </div>
            </div>
          )}
          <SmallInput value={colQuery} onChange={setColQuery} placeholder="Search columns" />
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
            {shownCols.length === 0 && <div style={{ color: C.fg5, fontSize: '.76rem', padding: 8 }}>{columns.length ? 'No match' : 'No columns known yet'}</div>}
            {shownCols.map((c) => {
              const dr = dateInfo.get(c.name);
              return (
                <div key={c.name}>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <button
                      onClick={() => mutate((t, a, b) => insertAtCursor(t, a, b, columnRef(c.name)))}
                      title={`Insert ${columnRef(c.name)}`}
                      style={{
                        display: 'block', flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent', border: 'none',
                        borderRadius: 6, padding: '5px 6px', cursor: 'pointer', color: C.fg2, fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ minWidth: 18, textAlign: 'center', fontSize: '.66rem', color: C.accentBright, background: 'rgba(59,130,246,.08)', borderRadius: 4, padding: '0 3px' }}>{TYPE_BADGE[c.type] || 'Aa'}</span>
                        <span style={{ fontFamily: MONO, fontSize: '.76rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      </span>
                      {textStored.has(c.name) && (
                        <span style={{ display: 'block', color: C.fg5, fontSize: '.66rem', marginLeft: 24 }}>stored as text, use num()</span>
                      )}
                    </button>
                    {dr && (
                      <button
                        onClick={() => setDateOpen(dateOpen === c.name ? null : c.name)}
                        title="These values look like dates. Click to see how to read them."
                        aria-expanded={dateOpen === c.name}
                        style={{
                          marginTop: 5, flexShrink: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: '.64rem', fontWeight: 700,
                          padding: '0 6px', borderRadius: 10, color: C.accentBright, background: 'transparent', border: `1px solid ${C.accentMuted}`,
                        }}
                      >date?</button>
                    )}
                  </div>
                  {dr && dateOpen === c.name && (
                    <div style={{ margin: '2px 4px 6px', padding: '6px 8px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: '.72rem', color: C.fg3 }}>
                      <div>{describeResult(dr)}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                        {insertOptions(dr).map((o) => (
                          <button key={o.id} onClick={() => insertDate(c.name, o.format)} style={{
                            textAlign: 'left', cursor: 'pointer', fontFamily: MONO, fontSize: '.7rem', padding: '3px 6px', borderRadius: 6,
                            color: C.accentBright, background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
                          }}>{o.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>

        {/* Editor + preview */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, padding: 14, overflowY: 'auto' }}>
          {isBody && convertConfig && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Button kind="accent" size="sm" onClick={convert} disabled={converting}
                  title="Turn this block's dropdown cases, values and else into code (only when it can be done exactly)">
                  {converting ? 'Converting…' : 'Convert from builder'}
                </Button>
                <span style={{ color: C.fg5, fontSize: '.72rem' }}>
                  {showConvertHint && !convertInfo ? FIRST_OPEN_HINT : 'The dropdown cases stay as they are.'}
                </span>
              </div>
              {convertInfo && convertInfo.ok && (
                <div style={{ marginTop: 6, fontSize: '.78rem' }}>
                  <div style={{ color: C.ok }}>Converted from the dropdown rules. Review it, then Apply to use it.</div>
                  {noticesBox(convertInfo.notices) && (
                    <div data-testid="convert-notices" style={{
                      marginTop: 8, background: 'var(--info-bg)', border: '1px solid var(--info-border)',
                      color: C.fg3, borderRadius: 8, padding: '8px 10px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.accentBright, fontWeight: 700 }}>
                        <Info size={14} /> {noticesBox(convertInfo.notices).heading}
                      </div>
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        {noticesBox(convertInfo.notices).items.map((n, i) => <li key={i} style={{ marginTop: 2 }}>{n}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {convertInfo && !convertInfo.ok && (
                <div style={{ marginTop: 6, background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem' }}>
                  <div style={{ fontWeight: 700 }}>{convertInfo.message || 'Can\'t convert this block exactly.'}</div>
                  {convertInfo.problems.map((p, i) => <div key={i} style={{ marginTop: 2 }}>{p}</div>)}
                  <div style={{ marginTop: 4, opacity: 0.85 }}>Nothing was changed.</div>
                </div>
              )}
            </div>
          )}
          {!isBody && hint && (
            <div style={{ color: C.fg4, fontSize: '.76rem', lineHeight: 1.5, marginBottom: 8 }}>{hint}</div>
          )}
          {isBody && (
            <div style={{ marginBottom: 8 }}>
              <button onClick={() => setShowHow(v => !v)} aria-expanded={showHow} style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                color: C.accentBright, fontSize: '.74rem', fontWeight: 600, fontFamily: 'inherit',
              }}>{showHow ? '▾' : '▸'} How this works</button>
              {showHow && (
                <div style={{ color: C.fg4, fontSize: '.76rem', lineHeight: 1.5, marginTop: 4 }}>
                  Write the logic as <b>if / else if / else</b>. Each <b>return</b> is the value for that row, and the first one that runs wins.
                  A row where no return runs stays blank. With more than one output column, return an object keyed by the output names.
                </div>
              )}
            </div>
          )}
          <div style={{ position: 'relative', background: C.input, borderRadius: 8 }}>
            {flash && flash.text === src && (
              <div ref={(el) => { backdropRef.current = el; }} aria-hidden="true" style={{
                position: 'absolute', inset: 0, boxSizing: 'border-box', padding: 10, border: '1px solid transparent',
                fontFamily: MONO, fontSize: '.84rem', lineHeight: 1.5, tabSize: 2, whiteSpace: 'pre', overflow: 'hidden',
                color: 'transparent', pointerEvents: 'none', borderRadius: 8,
              }}>
                {src.slice(0, flash.start)}
                <mark style={{ background: 'rgba(59,130,246,.35)', color: 'transparent', borderRadius: 3 }}>{src.slice(flash.start, flash.end)}</mark>
                {src.slice(flash.end)}
              </div>
            )}
          <textarea
            ref={taRef}
            data-autofocus
            value={src}
            onChange={(e) => { setSrc(e.target.value); setFlash(null); }}
            onScroll={(e) => { const bd = backdropRef.current; if (bd) { bd.scrollTop = e.target.scrollTop; bd.scrollLeft = e.target.scrollLeft; } }}
            onKeyDown={onTextKey}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            placeholder={isBody ? 'if (num(row.amount) > 1000) {\n  return "big";\n}\nreturn "small";' : 'e.g. num(row.amount) > 1000 && !isBlank(row.status)'}
            aria-label={isBody ? 'Code' : 'Expression'}
            style={{
              display: 'block', position: 'relative', width: '100%', boxSizing: 'border-box', minHeight: isBody ? 220 : 130, resize: 'vertical',
              background: 'transparent', whiteSpace: 'pre', overflow: 'auto', color: C.fg1, border: `1px solid ${vstate.state === 'invalid' ? 'var(--error-border)' : C.borderStrong}`,
              borderRadius: 8, padding: 10, fontFamily: MONO, fontSize: '.84rem', lineHeight: 1.5, outline: 'none', tabSize: 2,
            }}
          />
          </div>

          {/* validation line */}
          <div style={{ marginTop: 8, fontSize: '.8rem', minHeight: 20 }} role="status" aria-live="polite">
            {vstate.state === 'valid' && (
              <span style={{ color: C.ok, display: 'inline-flex', alignItems: 'center', gap: 6 }}><CheckCircle2 size={14} /> {isBody ? 'Valid' : 'Valid, returns true/false per row'}</span>
            )}
            {vstate.state === 'checking' && !clientError && (
              <span style={{ color: C.fg4, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Loader2 size={14} style={{ animation: 'omSpin 1s linear infinite' }} /> Checking…
              </span>
            )}
            {vstate.state === 'invalid' && (
              <span style={{ color: C.error, display: 'inline-flex', gap: 6, alignItems: 'flex-start' }}>
                <XCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span><b>{vstate.issue.title}</b>{vstate.issue.where ? ` — ${vstate.issue.where}` : ''}: {vstate.issue.message}</span>
              </span>
            )}
            {vstate.state === 'failed' && (
              <span style={{ color: C.error, display: 'inline-flex', gap: 6, alignItems: 'flex-start' }}>
                <XCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span><b>{vstate.failure.title}</b>: {vstate.failure.message}</span>
              </span>
            )}
            {vstate.state === 'offline' && (
              <span style={{ color: C.warn, display: 'inline-flex', gap: 6 }}><AlertTriangle size={14} /> Couldn’t check with the server: {vstate.message}</span>
            )}
            {clientError && vstate.state !== 'invalid' && (
              <span style={{ color: C.warn, display: 'inline-flex', gap: 6, alignItems: 'flex-start' }}>
                <AlertTriangle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>Possible syntax problem (line {clientError.line}, column {clientError.col}): {clientError.message}</span>
              </span>
            )}
          </div>

          {/* columns used */}
          {chipCols.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={{ color: C.fg5, fontSize: '.72rem' }}>Reads:</span>
              {chipCols.map((c) => {
                const bad = unknown.includes(c);
                return (
                  <span key={c} style={{
                    fontFamily: MONO, fontSize: '.72rem', padding: '1px 7px', borderRadius: 10,
                    color: bad ? C.warn : C.accentBright,
                    background: bad ? 'var(--warn-card-bg)' : 'rgba(59,130,246,.08)',
                    border: `1px solid ${bad ? 'var(--warn-border)' : 'transparent'}`,
                  }}>{c}</span>
                );
              })}
            </div>
          )}
          {current && unknown.length > 0 && (
            <div style={{ marginTop: 8, background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem', display: 'flex', gap: 8 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>Not in this step’s input: <b style={{ fontFamily: MONO }}>{unknown.join(', ')}</b>. They’ll read as blank unless an earlier step creates them.</span>
            </div>
          )}

          {/* live preview */}
          <div style={{ marginTop: 14, opacity: pending && shown ? 0.55 : 1, transition: 'opacity .15s' }}>
            <Eyebrow style={{ marginBottom: 6 }}>Live preview</Eyebrow>
            {isBody && outputError && (
              <div style={{ color: C.warn, fontSize: '.78rem' }}>Fix the output column names first: {outputError}</div>
            )}
            {rows.length === 0 && (
              <div style={{ color: C.fg5, fontSize: '.78rem' }}>No rows are available here yet, so the {isBody ? 'code' : 'expression'} is checked but not run. Run the pipeline preview once, or upload input data, to see the results.</div>
            )}
            {isBody && rows.length > 0 && !outputError && !(shown?.values) && (
              <div style={{ color: C.fg5, fontSize: '.78rem' }}>{blank ? 'Write the code to see the computed values.' : vstate.state === 'valid' || pending ? 'Evaluating…' : 'Fix the problem above to see values.'}</div>
            )}
            {isBody && rows.length > 0 && !outputError && shown?.values && (
              <ValueBodyPreview
                model={mapValueBodyResponse(shown, rows, outputs)}
                rowsNote={[rowsOrigin === 'server' ? 'Rows from the last pipeline preview' : null, rowsNote].filter(Boolean).join(' · ')}
              />
            )}
            {!isBody && rows.length > 0 && !shown?.flags && (
              <div style={{ color: C.fg5, fontSize: '.78rem' }}>{blank ? 'Write an expression to see which rows match.' : vstate.state === 'valid' || pending ? 'Evaluating…' : 'Fix the problem above to see matches.'}</div>
            )}
            {!isBody && shown?.flags && (() => {
              const s = previewSummary(shown);
              return (
                <>
                  <div style={{ color: C.fg2, fontSize: '.84rem', fontWeight: 600 }}>{s.headline}</div>
                  {(s.truncatedNote || rowsNote || rowsOrigin === 'server') && (
                    <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 2 }}>
                      {[s.truncatedNote, rowsOrigin === 'server' ? 'Rows from the last pipeline preview' : null, rowsNote].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {s.rowErrors && <ExampleLines title="Errors" count={s.rowErrors.count} examples={s.rowErrors.examples} />}
                  {s.nonBoolean && <ExampleLines title="Not true/false" count={s.nonBoolean.count} examples={s.nonBoolean.examples} />}
                </>
              );
            })()}
            {!isBody && listRows.length > 0 && (
              <div style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 8, maxHeight: 260, overflow: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.76rem' }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: C.bar }}>
                      <th style={{ textAlign: 'right', padding: '4px 8px', color: C.fg5, fontWeight: 600 }}>#</th>
                      {refCols.map(c => <th key={c} style={{ textAlign: 'left', padding: '4px 8px', color: C.fg4, fontFamily: MONO, fontWeight: 600 }}>{c}</th>)}
                      <th style={{ textAlign: 'left', padding: '4px 8px', color: C.fg4 }}>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listRows.map((r, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ textAlign: 'right', padding: '3px 8px', color: C.fg5 }}>{i + 1}</td>
                        {refCols.map(c => (
                          <td key={c} style={{ padding: '3px 8px', color: C.fg3, fontFamily: MONO, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r[c] == null || r[c] === '' ? <span style={{ color: C.fg6 }}>(blank)</span> : String(r[c])}
                          </td>
                        ))}
                        <td style={{ padding: '3px 8px', fontWeight: 700, color: current.flags[i] ? C.ok : C.fg5 }}>
                          {String(current.flags[i])}{flagged.has(i + 1) ? ' !' : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!isBody && current && listRows.length > 0 && current.total > ROW_LIST && (
              <div style={{ color: C.fg5, fontSize: '.7rem', marginTop: 4 }}>Showing the first {ROW_LIST} of {current.total} rows</div>
            )}
          </div>
        </div>

        {/* Snippets */}
        <Panel title="Snippets" style={{ borderLeft: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '0 12px 8px' }}>
            {(SNIPPET_CATEGORIES[kind] || []).map((cat) => {
              const on = category === cat.id;
              return (
                <button key={cat.id} onClick={() => setCategory(cat.id)} style={{
                  fontFamily: 'inherit', fontSize: '.7rem', fontWeight: 600, cursor: 'pointer',
                  padding: '3px 8px', borderRadius: 10,
                  background: on ? 'var(--info-bg)' : 'transparent',
                  color: on ? C.accentBright : C.fg4,
                  border: `1px solid ${on ? C.accentMuted : C.border}`,
                }}>{cat.label}</button>
              );
            })}
          </div>
          <SmallInput value={snipQuery} onChange={setSnipQuery} placeholder="Search snippets" />
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
            {snippets.length === 0 && <div style={{ color: C.fg5, fontSize: '.76rem', padding: 8 }}>No snippets match</div>}
            {snippets.map((s) => (
              <button
                key={s.id}
                onClick={() => mutate((t, a, b) => (isBody ? applySnippetIndented(t, a, b, s, { columns: colNames }) : applySnippet(t, a, b, s, { columns: colNames })))}
                title={s.example}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderRadius: 6, padding: '5px 6px', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <span style={{ display: 'block', color: C.fg2, fontSize: '.78rem', fontWeight: 600 }}>{s.label}</span>
                <span style={{ display: 'block', color: C.fg5, fontFamily: MONO, fontSize: '.68rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.code.replace(/«([^»]*)»/g, '$1')}</span>
              </button>
            ))}
          </div>
        </Panel>
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, background: C.bar }}>
        <span style={{ fontSize: '.74rem', color: len.over ? C.error : len.near ? C.warn : C.fg5 }} title={`Expressions are limited to ${MAX_EXPRESSION_CHARS} characters`}>
          {len.count} / {MAX_EXPRESSION_CHARS}
        </span>
        <div style={{ flex: 1 }} />
        <Button kind="ghost" onClick={requestClose}>Cancel</Button>
        <Button kind="primary" disabled={!canApply} title={applyTitle} onClick={() => onApply(src)}>Apply</Button>
      </div>
    </Modal>
  );
}
